// https://developers.intercom.com/installing-intercom/docs/basic-javascript
export function loadIntercom(hide_default_launcher = false) {
  window.intercomSettings = {
    api_base: 'https://api-iam.intercom.io',
    app_id: 'b0ht41y7',
    hide_default_launcher,
  };
  (function () {
    var w = window;
    var ic = w.Intercom;
    if (typeof ic === 'function') {
      ic('reattach_activator');
      ic('update', w.intercomSettings);
    } else {
      var d = document;
      var i = function () {
        i.c(arguments);
      };
      i.q = [];
      i.c = function (args) {
        i.q.push(args);
      };
      w.Intercom = i;
      var l = function () {
        var s = d.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = 'https://widget.intercom.io/widget/b0ht41y7';
        var x = d.getElementsByTagName('script')[0];
        x.parentNode.insertBefore(s, x);
      };
      if (document.readyState === 'complete') {
        l();
      } else if (w.attachEvent) {
        w.attachEvent('onload', l);
      } else {
        w.addEventListener('load', l, false);
      }
    }
  })();
}

// We need this explicit settings code otherwise
// windows.intercomSettings in loadIntercom will overwrite this
// this is racey as this is only relevant (ontop of the explict Intercom('update')
// when Intercom loads AFTER this is called
// TL;DR we need both the settings & the update
export function hideIntercom() {
  if (window.intercomSettings) {
    window.intercomSettings.hide_default_launcher = true;
  }
  window.Intercom?.('update', {
    hide_default_launcher: true,
  });
}

export function showIntercom() {
  if (window.intercomSettings) {
    window.intercomSettings.hide_default_launcher = false;
  }
  window.Intercom?.('update', {
    hide_default_launcher: false,
  });
}

/**
 * Hand the user off to Birdi's human support chat, optionally pre-filling the
 * composer with context (e.g. what Scout couldn't do for them).
 *
 * `showNewMessage` only POPULATES the composer — it never sends. The user
 * reviews and sends themselves, which is what keeps this a handoff rather
 * than the assistant messaging support on their behalf.
 *
 * Deliberately does NOT un-hide the default launcher. The launcher shows only
 * on the dashboard (index.tsx); everywhere else Scout is the way in, and
 * un-hiding here would leave the bubble stuck on non-dashboard pages after a
 * handoff. `Intercom('show')` opens the messenger regardless of launcher
 * visibility (Intercom's documented custom-launcher pattern), so the handoff
 * works from any page; the user reopens it the same way they got here (ask
 * Scout), and replies from support still surface as in-app message
 * notifications with the launcher hidden.
 *
 * Returns false when Intercom isn't available (not loaded, or
 * INTERCOM_ENABLED is off), so callers can fall back to an email address
 * instead of silently doing nothing.
 */
export function openSupportChat(prefill?: string): boolean {
  const w = window as Window & {
    Intercom?: (cmd: string, ...args: unknown[]) => void;
  };
  if (typeof w.Intercom !== 'function') return false;
  if (prefill) {
    w.Intercom('showNewMessage', prefill);
  } else {
    w.Intercom('show');
  }
  return true;
}

/**
 * After SPA content mounts (e.g. dashboard widgets), Intercom should re-scan the DOM for
 * tooltip/tour targets. Bare `update()` with no args is insufficient; match loadIntercom.
 */
export function refreshIntercomAfterContentChange(): void {
  const w = window as Window & {
    intercomSettings?: Record<string, unknown>;
    Intercom?: (cmd: string, ...args: unknown[]) => void;
  };
  if (typeof w.Intercom !== 'function') {
    return;
  }
  w.Intercom('reattach_activator');
  w.Intercom('update', w.intercomSettings ?? {});
}
